/**
 * Billing API client for the Auteur Cloud web app.
 *
 * Campaign `auteur-cloud-commercial-v1`, task C04C. This is the browser half of
 * the billing and entitlement surface built by C04A (`auteur_cloud/billing/**`,
 * `auteur_cloud/api/routes/billing.py`) and C04B
 * (`auteur_cloud/entitlements/**`), whose required implementation properties
 * this file exists to preserve rather than to re-derive:
 *
 * NO RAW STRIPE ID, EVER
 *   Every type below is a mirror of a server response shape observed in this
 *   repository at base ref `a721d3596...` (see the task handoff). None of them
 *   has a field for a Stripe customer id, subscription id, price id, or
 *   Checkout Session id — because the server never returns one
 *   (`billing.py`'s own module docstring: "No route accepts or returns a
 *   provider identifier"). This client cannot leak what it is never given.
 *
 * NO CLIENT-SIDE ENTITLEMENT OR BILLING AUTHORITY
 *   Every function here is a thin `fetch` around one server endpoint. None of
 *   them computes a status, a plan comparison, or a capability decision —
 *   `billing_state_projection()` and `EntitlementService.entitlement_report()`
 *   already did that server-side, and this file only relays what came back.
 *   The one exception (`fetchPricingCatalog`, see below) resolves a public
 *   product listing, not an entitlement.
 *
 * SESSION, NOT TOKEN
 *   Same posture as `api/auth.ts`: the `__Host-auteur_session` cookie is the
 *   only credential, it is `HttpOnly`, and every call sets
 *   `credentials: 'include'` so the browser attaches it. Nothing here reads or
 *   writes `localStorage`, `document.cookie`, or an `Authorization` header.
 *
 * NON-SECRET SESSION STORAGE
 *   `rememberPendingCheckoutSessionRef` / `consumePendingCheckoutSessionRef`
 *   use `sessionStorage` to carry this system's own opaque `session_ref`
 *   (never a provider id) across the redirect to Stripe-hosted Checkout and
 *   back. Checkout intent keys also survive a lost response in sessionStorage;
 *   neither value is an authentication credential or provider identifier.
 *
 * TWO ROUTES ARE A DOCUMENTED CONTRACT, NOT YET A WORKING SERVER
 *   `fetchPricingCatalog` (`GET /v1/billing/catalog`) and
 *   `fetchEntitlementReport` (`GET /v1/billing/entitlements`) call paths that
 *   do not exist in `auteur_cloud/api/routes/` at this base ref. C04C's owned
 *   files contain no route module, so this task cannot mount them — see the
 *   handoff's Risks section for the full explanation, which is the same
 *   "route exists, mount is somebody else's" shape C04A recorded for its own
 *   webhook route and C_VAULT/C_AUTH/`receipts`/`admin_ops` recorded before
 *   it. Both functions are written against the exact field names the owning
 *   Python modules (`commercial_product_catalog.py`,
 *   `auteur_cloud/entitlements/service.py::entitlement_report`) already
 *   return, so mounting a route that serializes that shape as JSON is the
 *   entire remaining integration step. Both pages that call them render an
 *   honest `Unavailable` state (never a fabricated catalog or entitlement)
 *   when the fetch 404s, which is exactly what happens today.
 *
 * BUILD STATUS
 *   Compiled and exercised as part of the Vite customer SPA.
 */

import { ApiClientError, requestJson } from './http';

const BASE = '/v1/billing';

/** The one shape every endpoint here returns on failure. */
export interface BillingApiErrorBody {
  code: string;
  message: string;
  status: number;
}

/** A structured failure. Mirrors `AuthApiError` in `api/auth.ts`. */
export class BillingApiError extends Error {
  readonly status: number;
  readonly code: string;

  constructor(status: number, code: string, message: string) {
    super(message);
    this.name = 'BillingApiError';
    this.status = status;
    this.code = code;
  }

  /** The endpoint is not mounted, or a required service is not configured. */
  get isUnavailable(): boolean {
    return this.status === 503 || this.status === 404;
  }
}

async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  try {
    return (await requestJson(`${BASE}${path}`, init)) as T;
  } catch (error) {
    if (error instanceof ApiClientError) {
      throw new BillingApiError(error.status, error.code, error.message);
    }
    throw error;
  }
}

// ---------------------------------------------------------------------------
// Pricing catalog — GET /v1/billing/catalog (contract; not yet mounted)
// ---------------------------------------------------------------------------

/**
 * One catalog entry's pricing block, mirroring `commercial_product_catalog
 * .pricing_for()`.
 *
 * `amount_minor` is typed `null` and nothing else: `pricing_policy` at
 * `config/commercial_products.yaml:134-157` forbids a literal amount in the
 * repository, and this client renders no other source for one. A resolved
 * amount, if this UI ever shows one, comes from a provider price lookup this
 * task does not perform.
 */
export interface CatalogEntryPricing {
  model: string;
  currency: string | null;
  amount_minor: null;
  billing_period: string | null;
  status: string;
}

/**
 * One row of `commercial_product_catalog.offers()` — `kind` in `offer`,
 * `edition`, `product`, `product_surface`. Deliberately NOT everything
 * `entries()` returns: an internal (`owner_private`) or not-yet-public
 * (anything short of `visibility: "public"`) row must never reach this
 * client, and that filtering is the server's job, not this file's — see
 * `PricingPage`'s own comment on why `visibility: "public"` is, by the
 * catalog's own enum comment, unreachable before `G_LEGAL`.
 */
export interface CatalogEntry {
  id: string;
  name: string;
  kind: string;
  visibility: string;
  grants: string[];
  pricing: CatalogEntryPricing;
  sellable: boolean;
  note?: string;
}

export async function fetchPricingCatalog(): Promise<CatalogEntry[]> {
  const body = await request<{ catalog: CatalogEntry[] }>('/catalog', { method: 'GET' });
  return body.catalog;
}

// ---------------------------------------------------------------------------
// Public plan list — GET /v1/billing/plans (no session required)
// ---------------------------------------------------------------------------

/** A public plan's price, in the catalog's minor units (cents for USD). */
export interface PublicPlanPricing {
  model: string;
  currency: string | null;
  amount_minor: number | null;
  billing_period: string | null;
}

/** Trial terms the server attaches to a plan; never inferred in the browser. */
export interface PublicPlanTrial {
  eligible: boolean;
  duration_days: number;
  card_required: boolean;
}

/**
 * One row of `GET /v1/billing/plans` — the signed-out Pricing page's list
 * (walkthrough F-34). Display fields only: the server serves exactly-public
 * catalog rows and never a grant key, visibility, approval status or price
 * reference. `id` is the offer's public handle, used to start checkout.
 */
export interface PublicPlan {
  id: string;
  name: string;
  note?: string;
  pricing: PublicPlanPricing;
  sellable: boolean;
  trial?: PublicPlanTrial;
}

export async function fetchPublicPlans(): Promise<PublicPlan[]> {
  const body = await request<{ plans: PublicPlan[] }>('/plans', { method: 'GET' });
  if (!body || !Array.isArray(body.plans)) {
    throw new BillingApiError(200, 'malformed_response', 'the plan list response is malformed');
  }
  return body.plans;
}

// ---------------------------------------------------------------------------
// Checkout — POST /v1/billing/checkout, GET /v1/billing/checkout/{session_ref}
// ---------------------------------------------------------------------------

/** Mirrors `auteur_cloud.billing.models.ENTITLEMENT_STATES`. */
export type EntitlementState = 'pending' | 'confirmed' | 'not_granted';

/**
 * What `POST /v1/billing/checkout` returns — mirrors
 * `auteur_cloud.billing.base.CheckoutHandoff`.
 *
 * `entitlement_state` is always `'pending'` here: this is the moment before
 * any provider webhook has arrived, and the required property this type
 * exists to enforce is that `redirect_url` is the *only* actionable field —
 * nothing in this shape ever says "you're subscribed".
 */
export interface CheckoutHandoff {
  session_ref: string;
  redirect_url: string;
  offer_id: string;
  entitlement_state: EntitlementState;
  notice: string;
}

/**
 * What `GET /v1/billing/checkout/{session_ref}` returns — mirrors the dict
 * built in `BillingService.checkout_status`. `granted` is the only boolean a
 * success page may act on, and it is `entitlement_state == 'confirmed'`,
 * written only by the webhook processor.
 */
export interface CheckoutStatus {
  session_ref: string;
  offer_id: string;
  status: string;
  entitlement_state: EntitlementState;
  granted: boolean;
  authority: 'server_derived';
  notice: string;
}

export interface StartCheckoutInput {
  offerId: string;
  trialRequested?: boolean;
  /** A PATH only — never a full URL. The server refuses anything else. */
  successPath?: string;
  cancelPath?: string;
}

const checkoutIntents = new Map<string, string>();
const PENDING_CHECKOUT_KEY = 'auteur_cloud.billing.pending_checkout_session_ref';
type BillingSessionStorageKey = typeof PENDING_CHECKOUT_KEY | `auteur.checkout.intent:${string}`;

/** The only direct sessionStorage access in this module. Values are opaque,
 * tab-scoped checkout references or idempotency keys; never credentials. */
function readBillingSessionValue(key: BillingSessionStorageKey): string | null {
  try { return window.sessionStorage.getItem(key); } catch { return null; }
}

function writeBillingSessionValue(key: BillingSessionStorageKey, value: string | null): void {
  try {
    if (value === null) window.sessionStorage.removeItem(key);
    else window.sessionStorage.setItem(key, value);
  } catch { /* Session storage is best effort; the in-memory path remains available. */ }
}

function checkoutIntent(input: StartCheckoutInput): string {
  return JSON.stringify([input.offerId, !!input.trialRequested, input.successPath || '', input.cancelPath || '']);
}

/** Forget a stuck intent after an explicit decision to start over. */
export function abandonCheckoutIntent(input: StartCheckoutInput): void {
  const intent = checkoutIntent(input);
  checkoutIntents.delete(intent);
  writeBillingSessionValue(`auteur.checkout.intent:${intent}`, null);
}

export async function startCheckout(input: StartCheckoutInput): Promise<CheckoutHandoff> {
  const intent = checkoutIntent(input);
  const storageKey: `auteur.checkout.intent:${string}` = `auteur.checkout.intent:${intent}`;
  let key = checkoutIntents.get(intent) || readBillingSessionValue(storageKey) || undefined;
  if (!key) {
    key = crypto.randomUUID();
    checkoutIntents.set(intent, key);
    writeBillingSessionValue(storageKey, key);
  }
  const body = await request<{ checkout: CheckoutHandoff }>('/checkout', {
    method: 'POST',
    headers: { 'idempotency-key': key },
    body: JSON.stringify({
      offer_id: input.offerId,
      ...(input.trialRequested ? { trial_requested: true } : {}),
      success_path: input.successPath,
      cancel_path: input.cancelPath,
    }),
  });
  if (typeof body.checkout?.session_ref !== 'string' || !body.checkout.session_ref ||
      typeof body.checkout.redirect_url !== 'string' || !body.checkout.redirect_url ||
      body.checkout.entitlement_state !== 'pending') {
    throw new BillingApiError(200, 'malformed_response', 'checkout did not return a pending handoff');
  }
  checkoutIntents.delete(intent);
  writeBillingSessionValue(storageKey, null);
  return body.checkout;
}

export async function checkoutStatus(sessionRef: string): Promise<CheckoutStatus> {
  const body = await request<{ checkout: CheckoutStatus }>(
    `/checkout/${encodeURIComponent(sessionRef)}`,
    { method: 'GET' },
  );
  return body.checkout;
}

// ---------------------------------------------------------------------------
// Portal — POST /v1/billing/portal
// ---------------------------------------------------------------------------

/** Mirrors `auteur_cloud.billing.base.PortalHandoff` — a URL, nothing else. */
export interface PortalHandoff {
  redirect_url: string;
  expires_at: string | null;
}

export async function openBillingPortal(returnPath?: string): Promise<PortalHandoff> {
  const body = await request<{ portal: PortalHandoff }>('/portal', {
    method: 'POST',
    body: JSON.stringify({ return_path: returnPath }),
  });
  return body.portal;
}

// ---------------------------------------------------------------------------
// Billing state — GET /v1/billing/state
// ---------------------------------------------------------------------------

/** One row of `billing_state_projection()`'s `invoices` list. */
export interface InvoiceProjection {
  status: string;
  currency: string;
  amount_due_minor: number;
  amount_paid_minor: number;
  attempt_count: number;
  period_start: string | null;
  period_end: string | null;
  paid_at: string | null;
  payment_failed_at: string | null;
}

/**
 * Mirrors `auteur_cloud.billing.models.billing_state_projection()` exactly —
 * the whole reason this type has no provider-identifier field is that the
 * function it mirrors was written specifically not to have one. `subscribed:
 * false` is the shape for "no subscription exists"; every other field is
 * optional in that state, matching the projection's own two-branch return.
 */
export interface BillingState {
  subscribed: boolean;
  status: string;
  provider_status?: string;
  paid_service: boolean;
  offer_id?: string;
  cancel_at_period_end: boolean;
  current_period_start?: string | null;
  current_period_end: string | null;
  trial_start?: string | null;
  trial_end: string | null;
  canceled_at?: string | null;
  automatic_tax?: boolean;
  tax_status: string;
  invoices: InvoiceProjection[];
  authority: 'server_derived';
}

export async function fetchBillingState(): Promise<BillingState> {
  const body = await request<{ billing: BillingState }>('/state', { method: 'GET' });
  return body.billing;
}

// ---------------------------------------------------------------------------
// Entitlement report — GET /v1/billing/entitlements (contract; not yet mounted)
// ---------------------------------------------------------------------------

export interface EntitlementPlan {
  plan_code: string;
  display_name: string;
  catalog_entry_id: string;
  device_limit: number | null;
  offline_grace_days: number | null;
  dunning_grace_days: number | null;
  document_ttl_days: number | null;
}

export interface EntitlementGrant {
  grant_id: string;
  source: string;
  plan_code: string;
  granted_at: string;
  billing_ref: string;
}

/**
 * The `subscription` block — mirrors `policy.LifecycleProjection.to_payload()`.
 * `lifecycle` is the precise, dunning-aware state
 * (`not_subscribed|incomplete|trialing|active|payment_failed|suspended|
 * cancel_pending|ended`); `projects_to_authority_state` is where it lands in
 * the frozen `entitlements.AUTHORITY_STATES` vocabulary
 * `BILLING_UX_SPEC.md` §5's table was written against. `BillingPage` renders
 * from `lifecycle` first, precisely because that table predates C04B's
 * dunning projection — see the handoff for the reconciliation.
 */
export interface EntitlementSubscription {
  lifecycle: string;
  status: string;
  paid_service: boolean;
  entitlement_ends_at: string;
  grace_ends_at: string;
  recovery_action: string;
  detail: string;
  projects_to_authority_state: string;
}

export interface EntitlementDevices {
  limit: number | null;
  active: string[];
  revoked: string[];
  last_check_in: string;
}

/** A signed local license document's metadata — never its signature bytes. */
export interface EntitlementDocument {
  license_id: string;
  issued_at: string;
  expires_at: string | null;
  signing_key_id: string | null;
  payload_sha256: string;
  revoked: boolean;
  revoked_reason: string;
}

/**
 * Mirrors `EntitlementService.entitlement_report()` field for field. This is
 * the type `components/Entitlements` renders without computing anything from
 * it — see that component's own file comment for why
 * `constitutional_capabilities` is treated specially.
 */
export interface EntitlementReport {
  org_id: string;
  mode: 'passive' | 'enforced';
  enforced: boolean;
  store_available: boolean;
  reason_code: string;
  plan: EntitlementPlan | null;
  grant: EntitlementGrant | null;
  subscription: EntitlementSubscription;
  plan_capabilities: string[];
  effective_capabilities: string[];
  constitutional_capabilities: string[];
  devices: EntitlementDevices;
  document: EntitlementDocument | null;
  authority: 'server_derived';
  projects_to_authority_state: string;
}

export async function fetchEntitlementReport(): Promise<EntitlementReport> {
  const body = await request<{ entitlement: EntitlementReport }>('/entitlements', {
    method: 'GET',
  });
  return body.entitlement;
}

// ---------------------------------------------------------------------------
// The pending-checkout reference — stored only through the scoped helpers above
// ---------------------------------------------------------------------------

/**
 * Remember this system's own `session_ref` across the redirect to
 * Stripe-hosted Checkout and back.
 *
 * Why `sessionStorage`, when `api/auth.ts` and `LoginPage.tsx` forbid it: I-7
 * forbids `sessionStorage` as an IDENTITY source — something that could stand
 * in for the session cookie. `session_ref` is not that. It grants nothing by
 * itself: `checkoutStatus` re-authorizes against the caller's own session and
 * org on every call (`billing.py`'s own docstring — "No route accepts an
 * `org_id`... There is no parameter through which a caller could name another
 * tenant"), and it names no provider object. An attacker who can read this
 * tab's `sessionStorage` can already ride the session cookie directly, so
 * this reference adds no new capability to steal. What it buys is real: the
 * server never embeds `session_ref` in the Stripe-bound `success_url` (only a
 * bare PATH crosses that boundary, deliberately, so the return trip cannot
 * carry a provider-controlled query string this app would trust) — so
 * without this, the success page would have no way to know which checkout to
 * poll.
 */
export function rememberPendingCheckoutSessionRef(sessionRef: string): void {
  writeBillingSessionValue(PENDING_CHECKOUT_KEY, sessionRef);
}

/** Read and clear the pending reference. Cleared even if the caller never asks again. */
export function consumePendingCheckoutSessionRef(): string | null {
  const value = readBillingSessionValue(PENDING_CHECKOUT_KEY);
  writeBillingSessionValue(PENDING_CHECKOUT_KEY, null);
  return value;
}

/**
 * The complete set of billing operations available to this client, exported
 * as data so the contract test can assert the set rather than grep for
 * absences — same pattern as `providerKeysApi.js`'s `PROVIDER_KEY_OPERATIONS`.
 */
export const BILLING_API_OPERATIONS = Object.freeze([
  'catalog.list',
  'plans.list',
  'checkout.start',
  'checkout.abandon',
  'checkout.status',
  'portal.open',
  'state.read',
  'entitlements.read',
]);
